import type { TemplatePackRead } from '../../types/api';

/**
 * A blueprint of one pack's layout, drawn as line-work on the tile.
 *
 * Shown while a deployment has no rendered preview (and behind a preview that
 * fails to load). Like `ThemeSwatch` it is deliberately abstract — the honest
 * thing to promise is the *shape* the pack draws: a portrait disc over a name
 * rule, a grid of discs, menu rules with a price stub, a display numeral over
 * tick rows. A spec sketch, not a fake post.
 */
export function PackSwatch({ pack, large = false }: { pack: TemplatePackRead; large?: boolean }) {
  const className = large ? 'pack-swatch pack-swatch--large' : 'pack-swatch';

  if (pack.layout === 'profile_grid') {
    return (
      <span className={className} aria-hidden="true">
        <span className="pack-swatch__rule pack-swatch__rule--title" />
        <span className="pack-swatch__grid">
          {[0, 1, 2, 3].map((cell) => (
            <span className="pack-swatch__cell" key={cell}>
              <span className="pack-swatch__disc pack-swatch__disc--small" />
              <span className="pack-swatch__rule pack-swatch__rule--cell" />
            </span>
          ))}
        </span>
      </span>
    );
  }

  if (pack.layout === 'menu_list') {
    return (
      <span className={className} aria-hidden="true">
        <span className="pack-swatch__rule pack-swatch__rule--title" />
        {[0, 1, 2].map((row) => (
          <span className="pack-swatch__menu-row" key={row}>
            <span className="pack-swatch__rule pack-swatch__rule--dish" />
            <span className="pack-swatch__leader" />
            <span className="pack-swatch__rule pack-swatch__rule--price" />
          </span>
        ))}
      </span>
    );
  }

  if (pack.layout === 'offer') {
    return (
      <span className={className} aria-hidden="true">
        <span className="pack-swatch__numeral">50%</span>
        {[0, 1].map((row) => (
          <span className="pack-swatch__tick-row" key={row}>
            <span className="pack-swatch__tick">✓</span>
            <span className="pack-swatch__rule pack-swatch__rule--benefit" />
          </span>
        ))}
      </span>
    );
  }

  if (pack.layout === 'anniversary') {
    // The milestone poster: the photo arch, the stacked name, the badge.
    return (
      <span className={className} aria-hidden="true">
        <span className="pack-swatch__arch" />
        <span className="pack-swatch__rule pack-swatch__rule--name pack-swatch__rule--beside" />
        <span className="pack-swatch__badge" />
      </span>
    );
  }

  // profile — the portrait layouts (welcome, product hero).
  return (
    <span className={className} aria-hidden="true">
      <span className="pack-swatch__disc" />
      <span className="pack-swatch__rule pack-swatch__rule--name" />
      <span className="pack-swatch__rule pack-swatch__rule--role" />
    </span>
  );
}
